import React, { useState, useEffect, useCallback } from "react";
import { Transition } from "@headlessui/react";
import { fetchWithAuth } from "../utils/apiHelper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const TomorrowTurnosModal = ({ isOpen, onClose }) => {
  const [data, setData] = useState({ clients: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openClients, setOpenClients] = useState({}); // For toggling details
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("notSent"); // "all", "sent", or "notSent"

  // 1) FETCH CLIENTS: Fetch clients and assign an "allNotified" flag to each client
  const fetchClients = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchWithAuth("/clientes/tomorrow-turnos");
      const processedClients = result.clients.map((client) => {
        const allNotified = client.turnos.every((t) => t.link_enviado === true);
        return { ...client, allNotified };
      });
      setData({ clients: processedClients });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // 2) Load data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchClients();
    }
  }, [isOpen, fetchClients]);

  // Remove early return; let Transition handle the mounting/unmounting
  // if (!isOpen) return null;

  // Helpers to format time and date
  const formatTime = (time) => time?.substring(0, 5);
  const formatDate = (date) => {
    if (!date) return "";
    const [year, month, day] = date.split("-");
    return `${day}/${month}`;
  };

  // 3) HANDLE NOTIFY: Notifies via WhatsApp and updates the API (or just resends)
  const handleNotify = async (client) => {
    const { cliente_id, cliente_nombre, turnos, telefono, allNotified } = client;
    const turnoIds = turnos.map(turno => turno.turno_id).join(",");

    let message = `Hola ${cliente_nombre}, ¿cómo estás?\n\n`;
    message += `Te recordamos que tenés ${turnos.length > 1 ? "los siguientes turnos" : "un turno"} agendado${turnos.length > 1 ? "s" : ""} para mañana:\n\n`;

    turnos.forEach((turno) => {
      message += `*${formatDate(turno.fecha)}* a las ${formatTime(turno.hora)}hs - Servicio: *${turno.servicio}*\n`;
    });

    message += `Para confirmar o cancelar tu asistencia, ingresá aquí:\n`;
    message += `${process.env.REACT_APP_API_HOST}/confirmar/turno/${turnoIds}\n\n`;
    message += `Si necesitás reprogramar o cancelar tu cita, escribinos. ¡Estamos para ayudarte!\n\n`;
    message += `Gracias por elegirnos, ${cliente_nombre}. ¡Nos vemos pronto!`;

    const whatsAppLink = `https://wa.me/${telefono}?text=${encodeURIComponent(message)}`;
    window.open(whatsAppLink, "_blank");

    // If not already notified, update the state in your API
    if (!allNotified) {
      try {
        await fetchWithAuth("/clientes/notificar-cliente", {
          method: "POST",
          body: JSON.stringify({ cliente_id }),
        });
        toast.success(`Cliente ${cliente_nombre} notificado correctamente`);
        fetchClients();
      } catch (err) {
        console.error("Error al marcar turnos como notificados:", err);
        toast.error("Error al actualizar el estado del cliente");
      }
    } else {
      // If already notified, just show an informational toast
      toast.info(`Mensaje reenviado a ${cliente_nombre}`);
    }
  };

  

  // 4) HANDLE MANUAL: Mark a client as notified manually (without WhatsApp)
  const handleMarkManual = async (client) => {
    const { cliente_id, cliente_nombre, cliente_apellido } = client;
    try {
      await fetchWithAuth("/clientes/notificar-cliente", {
        method: "POST",
        body: JSON.stringify({ cliente_id }),
      });
      toast.success(
        `Cliente ${cliente_nombre} ${cliente_apellido} marcado manualmente como notificado`
      );
      fetchClients();
    } catch (err) {
      console.error("Error al actualizar estado manualmente:", err);
      toast.error("Error al actualizar el estado manualmente");
    }
  };

  // 5) TOGGLE CLIENT DETAILS
  const toggleClientDetails = (clientId) => {
    setOpenClients((prev) => ({
      ...prev,
      [clientId]: !prev[clientId],
    }));
  };

  // 6) FILTER: Search and filter clients by state (all, sent, not sent)
  const filteredClients = data.clients.filter((client) => {
    const matchesSearch =
      client.cliente_nombre?.toLowerCase().includes(search.toLowerCase()) ||
      client.cliente_apellido?.toLowerCase().includes(search.toLowerCase());
    let matchesFilter = true;
    if (filter === "sent") {
      matchesFilter = client.allNotified;
    } else if (filter === "notSent") {
      matchesFilter = !client.allNotified;
    }
    return matchesSearch && matchesFilter;
  });

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />
      {/* Always render Transition; it controls visibility using the show prop */}
      <Transition show={isOpen} appear as="div">
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4">
          <Transition.Child
            as="div" // Ensure this renders a valid DOM element
            appear
            enter="transition ease-out duration-300 transform"
            enterFrom="scale-95 opacity-0"
            enterTo="scale-100 opacity-100"
            leave="transition ease-in duration-200 transform"
            leaveFrom="scale-100 opacity-100"
            leaveTo="scale-95 opacity-0"
          >
            {/* Modal Container */}
            <div className="relative bg-gray-900 text-white rounded-xl shadow-lg w-full max-w-3xl p-6">
              {/* Header */}
              <div className="flex justify-between items-center border-b border-gray-700 pb-3">
                <h2 className="text-2xl font-semibold">Turnos de Mañana</h2>
                <button
                  onClick={onClose}
                  aria-label="Cerrar modal"
                  className="text-gray-400 hover:text-red-500 text-xl"
                >
                  ✕
                </button>
              </div>

              {/* Filters: Search and State */}
              <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <input
                  type="text"
                  placeholder="Buscar cliente..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full sm:w-1/2 px-4 py-2 bg-gray-800 text-white rounded-lg outline-none border border-gray-600 focus:border-blue-500"
                />
                <div className="mt-2 sm:mt-0 flex gap-4">
                  <button
                    onClick={() => setFilter("all")}
                    className={`px-3 py-1 rounded ${
                      filter === "all"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-700 text-gray-300"
                    }`}
                  >
                    Todos
                  </button>
                  <button
                    onClick={() => setFilter("notSent")}
                    className={`px-3 py-1 rounded ${
                      filter === "notSent"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-700 text-gray-300"
                    }`}
                  >
                    No Enviados
                  </button>
                  <button
                    onClick={() => setFilter("sent")}
                    className={`px-3 py-1 rounded ${
                      filter === "sent"
                        ? "bg-blue-600 text-white"
                        : "bg-gray-700 text-gray-300"
                    }`}
                  >
                    Enviados
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="mt-4 max-h-96 overflow-y-auto space-y-3">
                {loading ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin h-8 w-8 border-t-2 border-b-2 border-blue-500 rounded-full"></div>
                  </div>
                ) : error ? (
                  <p className="text-center text-red-400">Error: {error}</p>
                ) : filteredClients.length > 0 ? (
                  filteredClients.map((client) => {
                    const {
                      cliente_id,
                      cliente_nombre,
                      cliente_apellido,
                      telefono,
                      turnos,
                      allNotified,
                    } = client;
                    return (
                      <div
                        key={cliente_id}
                        className="p-4 bg-gray-800 rounded-lg shadow-md transition hover:bg-gray-700"
                      >
                        {/* Client Header */}
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="text-lg font-medium flex items-center gap-2">
                              {cliente_nombre} {cliente_apellido}
                            </h3>
                            <p className="text-gray-400 text-sm">📞 {telefono}</p>
                          </div>
                          {/* Notification Badge */}
                          <div>
                            {allNotified ? (
                              <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-md">
                                Enviados
                              </span>
                            ) : (
                              <span className="px-3 py-1 bg-red-600 text-white text-sm rounded-md">
                                No Enviados
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 items-center mt-2">
                          <button
                            onClick={() => handleNotify(client)}
                            className="px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-500 transition"
                          >
                            {allNotified ? "Reenviar" : "Notificar"}
                          </button>
                          {/* Show "Marcar Manual" only if not yet notified */}
                          {!allNotified && (
                            <button
                              onClick={() => handleMarkManual(client)}
                              className="px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-500 transition"
                            >
                              Marcar Manual
                            </button>
                          )}
                          <button
                            onClick={() => toggleClientDetails(cliente_id)}
                            className="px-3 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-500 transition"
                          >
                            {openClients[cliente_id] ? "Ocultar Turnos" : "Ver Turnos"}
                          </button>
                        </div>


                        {/* Turnos Details */}
                        {openClients[cliente_id] && turnos.length > 0 && (
                          <div className="mt-3 bg-gray-700 p-3 rounded-lg">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b border-gray-600 text-gray-300">
                                  <th className="py-1 text-left">Fecha</th>
                                  <th className="py-1 text-left">Hora</th>
                                  <th className="py-1 text-left">Servicio</th>
                                  <th className="py-1 text-left">Profesional</th>
                                  <th className="py-1 text-left">Estado</th>
                                </tr>
                              </thead>
                              <tbody>
                                {turnos.map((turno) => (
                                  <tr
                                    key={turno.turno_id}
                                    className="border-b border-gray-600 last:border-0"
                                  >
                                    <td className="py-1">
                                      {formatDate(turno.fecha)}
                                    </td>
                                    <td className="py-1">
                                      {formatTime(turno.hora)}
                                    </td>
                                    <td className="py-1">{turno.servicio}</td>
                                    <td className="py-1">{turno.profesional}</td>
                                    <td className="py-1">{turno.estado}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <p className="text-center text-gray-400">
                    No hay clientes para el filtro seleccionado.
                  </p>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end mt-4">
                <button
                  onClick={onClose}
                  className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition"
                >
                  Cerrar
                </button>
              </div>
            </div>
          </Transition.Child>
        </div>
      </Transition>
    </>
  );
};

export default TomorrowTurnosModal;
